import { createSlice, PayloadAction, Reducer } from '@reduxjs/toolkit';
import type { OutcomeTotals } from '@root/lib';
import type { LudoJourneyStatus } from '@root/store/types';

export const APP_SLICE_KEY = 'app';

export interface AppSliceState {
  menuPopupOpen: boolean;
  customBetPopupOpen: boolean;
  gameLimitsModalOpen: boolean;
  myBetsHistoryModalOpen: boolean;
  gameRulesModalOpen: boolean;
  gameInfoModalOpen: boolean;
  rollRejectedOpen: boolean;
  runResultOpen: boolean;
  runResultOutcome: LudoJourneyStatus | null;
  // Taken when the run ends: the next run is seeded a second later and the
  // figures on the live frame are already its own.
  runResultTotals: OutcomeTotals | null;
  // Captured when the card opens, or it would follow the next roll's round.
  runResultRoundId: string | null;
}

const initialState: AppSliceState = {
  menuPopupOpen: false,
  customBetPopupOpen: false,
  gameLimitsModalOpen: false,
  myBetsHistoryModalOpen: false,
  gameRulesModalOpen: false,
  gameInfoModalOpen: false,
  rollRejectedOpen: false,
  runResultOpen: false,
  runResultOutcome: null,
  runResultTotals: null,
  runResultRoundId: null,
};

const appSlice = createSlice({
  name: APP_SLICE_KEY,
  initialState,
  reducers: {
    setMenuPopupOpen: (state, action: PayloadAction<boolean>) => {
      state.menuPopupOpen = action.payload;
    },
    setCustomBetPopupOpen: (state, action: PayloadAction<boolean>) => {
      state.customBetPopupOpen = action.payload;
    },
    setGameLimitsModalOpen: (state, action: PayloadAction<boolean>) => {
      state.gameLimitsModalOpen = action.payload;
    },
    setMyBetsHistoryModalOpen: (state, action: PayloadAction<boolean>) => {
      state.myBetsHistoryModalOpen = action.payload;
    },
    setRollRejectedOpen: (state, action: PayloadAction<boolean>) => {
      state.rollRejectedOpen = action.payload;
    },
    setGameRulesModalOpen: (state, action: PayloadAction<boolean>) => {
      state.gameRulesModalOpen = action.payload;
    },
    setGameInfoModalOpen: (state, action: PayloadAction<boolean>) => {
      state.gameInfoModalOpen = action.payload;
    },
    openRunResult: (
      state,
      action: PayloadAction<{
        outcome: LudoJourneyStatus;
        roundId: string;
        totals: OutcomeTotals;
      }>
    ) => {
      state.runResultOpen = true;
      state.runResultOutcome = action.payload.outcome;
      state.runResultTotals = action.payload.totals;
      state.runResultRoundId = action.payload.roundId;
    },
    closeRunResult: (state) => {
      state.runResultOpen = false;
      state.runResultOutcome = null;
      state.runResultTotals = null;
      state.runResultRoundId = null;
    },
  },
});

const appReducer = appSlice.reducer as Reducer;
export default appReducer;

export const appSelector = (state: any): AppSliceState => state[APP_SLICE_KEY];

export const appActions = appSlice.actions;
